import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { useTranslation } from 'react-i18next';
import { holdingsApi, type Holding, type HoldingValuation } from '@/api/holdings';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ErrorState } from '@/components/ErrorState';
import { KindChip } from '@/components/KindChip';
import { MoneyText } from '@/components/MoneyText';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { toCivil } from '@/features/envelope/window';
import { formatCivilDate, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const TITLE_ID = 'holding-valuation-title';

interface ValuationDialogProps {
  holding: Holding;
  operatingCurrency: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function ValuationDialog({
  holding,
  operatingCurrency,
  onClose,
  onSaved,
}: ValuationDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fixed = holding.type === 'FIXED_ASSET';
  const [asOf, setAsOf] = useState(toCivil(new Date()));
  const [amount, setAmount] = useState('');
  const [history, setHistory] = useState<HoldingValuation[] | null>(null);
  const [historyError, setHistoryError] = useState<{ cause: unknown } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [asOfError, setAsOfError] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const started = useRef(false);
  const { dirty } = useDirty({ asOf, amount });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryError(null);
      setHistory(await holdingsApi.listValuations(holding.id));
    } catch (error) {
      setHistoryError({ cause: error });
      setHistory([]);
    }
  }, [holding.id]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void loadHistory();
  }, [loadHistory]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(asOf);
    const minor = parseMoneyToMinor(amount, operatingCurrency, locale);
    setAsOfError(!dateOk);
    setAmountError(minor === null);
    if (!dateOk || minor === null) {
      guard.focusErrors();
      return;
    }
    setSubmitting(true);
    setSubmitMessage(null);
    try {
      await holdingsApi.addValuation(
        holding.id,
        fixed ? { asOf, valueMinor: minor } : { asOf, currentPriceMinor: minor },
      );
      await holdingsApi.get(holding.id);
      await holdingsApi.listValuations(holding.id);
      await onSaved();
      onClose();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        setSubmitMessage(t('holdings.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{ transition: { onEntered: () => amountRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>
          {fixed
            ? t('holdings.valueTitle', { name: holding.name })
            : t('holdings.markTitle', { name: holding.name })}
        </DialogTitle>
        <DialogContent>
          {subscribeRequired ? (
            <SubscribeInPlace onClose={onClose} />
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {submitMessage !== null && <Alert severity="error">{submitMessage}</Alert>}
              <Typography variant="body2">
                {fixed ? t('holdings.valueBody') : t('holdings.markBody')}
              </Typography>
              <TextField
                type="date"
                label={t('holdings.asOfLabel')}
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
                required
                fullWidth
                error={asOfError}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label={fixed ? t('holdings.valueAmountLabel') : t('holdings.markAmountLabel')}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                inputRef={amountRef}
                required
                fullWidth
                error={amountError}
              />
              <Typography variant="subtitle2" component="h3">
                {t('holdings.history')}
              </Typography>
              {historyError !== null && (
                <ErrorState
                  variant="section"
                  error={historyError.cause}
                  what={t('errors.what.valuationHistory')}
                  onRetry={() => void loadHistory()}
                />
              )}
              {history !== null && history.length === 0 && historyError === null && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('holdings.noValuations')}
                </Typography>
              )}
              {history !== null && history.length > 0 && (
                <Stack spacing={1}>
                  {history.map((row) => (
                    <Stack
                      key={row.id}
                      direction="row"
                      sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <TabularText>{formatCivilDate(row.asOf, locale)}</TabularText>
                      <MoneyText minor={row.valueMinor} currency={operatingCurrency} />
                      <KindChip neutral label={t(`holdings.source.${row.source}`)} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        {!subscribeRequired && (
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <SubmitButton type="submit" loading={submitting}>
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        )}
      </form>
    </GuardedDialog>
  );
}
